import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Contact, FileSpreadsheet, RotateCcw, UserPlus } from 'lucide-react';
import {
  Button,
  DataTable,
  DeleteConfirmDialog,
  ExcelUploadDialog,
  Tabs,
  TabsList,
  TabsTrigger,
  TooltipProvider,
  createActionsColumn,
  createColumnHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';
import type {
  CreatePartnerContactRequest,
  PartnerContact,
  PartnerContactOutcome,
  UpdatePartnerContactRequest
} from '@bliks-io/contracts';
import { usePartnerContacts } from '@/pages/partner/api/queries/usePartnerContacts';
import {
  useCreatePartnerContact,
  useDeletePartnerContact,
  useUpdatePartnerContact
} from '@/pages/partner/api/mutations/usePartnerContacts';
import { PartnerErrorState } from '@/pages/partner/components/PartnerErrorState';
import { PartnerPageHeader } from '@/pages/partner/components/PartnerPageHeader';
import { PartnerContactDialog } from './PartnerContactDialog';
import { NewLeadFlow } from './NewLeadFlow';
import { useIsSalesActivated } from './useIsSalesActivated';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { downloadPartnerContactsTemplate } from '@/lib/excel';
import { ContactImportPreviewDialog } from '@/pages/partner/contacts/ContactImportPreviewDialog';
import { ContactOutcomeCell } from '@/pages/partner/contacts/ContactOutcomeCell';
import { NoInterestDialog } from '@/pages/partner/contacts/NoInterestDialog';
import { effectiveOutcome, matchContactsToLeads } from '@/pages/partner/contacts/contactLeadMatch';
import { usePartnerLeads } from '@/pages/partner/api/queries/usePartnerLeads';
import {
  parsePartnerContactsFromExcel,
  previewContactImport,
  type ContactImportPreview
} from '@/pages/partner/contacts/parsePartnerContacts';
import { PartnerSalesTabsNav } from '@/pages/partner/components/PartnerSalesTabsNav';

type StatusFilter = 'open' | 'lead' | 'noInterest' | 'all';

function matchesStatus(outcome: PartnerContactOutcome | undefined, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'open') return !outcome;
  return outcome === filter;
}

/** An edit sends the whole form; emptied optional fields must clear, not linger. */
function toUpdate(values: CreatePartnerContactRequest): UpdatePartnerContactRequest {
  return {
    ...values,
    email: values.email ?? null,
    phone: values.phone ?? null,
    note: values.note ?? null
  };
}

export function PartnerContactsPage() {
  const { t } = useTranslation();
  const {
    data: contacts = [],
    isLoading,
    isError,
    error,
    isFetching,
    refetch
  } = usePartnerContacts();
  // Leads 403 until sales is activated; then nothing matches and the list still works.
  const { data: leadsResponse } = usePartnerLeads();
  const createContact = useCreatePartnerContact();
  const updateContact = useUpdatePartnerContact();
  const deleteContact = useDeletePartnerContact();
  const isSalesActivated = useIsSalesActivated();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PartnerContact | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [leadSource, setLeadSource] = useState<PartnerContact | null>(null);
  const [closing, setClosing] = useState<PartnerContact | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [importPreview, setImportPreview] = useState<ContactImportPreview | null>(null);

  const showError = (err: unknown) => toastApiError(err, 'common.Error');

  const closeAsNoInterest = (contact: PartnerContact, note: string) =>
    updateContact.mutate(
      { id: contact._id, data: { outcome: 'noInterest', note: note.trim() || null } },
      { onSuccess: () => setClosing(null), onError: showError }
    );
  const reopen = (contact: PartnerContact) =>
    updateContact.mutate({ id: contact._id, data: { outcome: null } }, { onError: showError });

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleSubmit = (values: CreatePartnerContactRequest) => {
    const onSuccess = () => setDialogOpen(false);
    if (editing) {
      updateContact.mutate(
        { id: editing._id, data: toUpdate(values) },
        { onSuccess, onError: showError }
      );
    } else {
      createContact.mutate(values, { onSuccess, onError: showError });
    }
  };

  const leadMatches = useMemo(
    () => matchContactsToLeads(contacts, leadsResponse?.leads ?? []),
    [contacts, leadsResponse]
  );
  const outcomeOf = (contact: PartnerContact) =>
    effectiveOutcome(contact, leadMatches.get(contact._id));

  const visibleContacts = contacts.filter((contact) =>
    matchesStatus(outcomeOf(contact), statusFilter)
  );

  const columns: DataTableColumnDef<PartnerContact>[] = [
    {
      id: 'name',
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      header: createColumnHeader(t('partner.contacts.columns.name')),
      cell: ({ row, getValue }) => (
        <span className={outcomeOf(row.original) ? 'text-muted-foreground' : 'font-medium'}>
          {getValue<string>()}
        </span>
      )
    },
    {
      accessorKey: 'company',
      header: createColumnHeader(t('partner.contacts.columns.company'))
    },
    {
      accessorKey: 'email',
      header: createColumnHeader(t('partner.contacts.columns.email')),
      cell: ({ row }) =>
        row.original.email ? (
          <a className="hover:underline" href={`mailto:${row.original.email}`}>
            {row.original.email}
          </a>
        ) : (
          '—'
        )
    },
    {
      accessorKey: 'phone',
      header: createColumnHeader(t('partner.contacts.columns.phone')),
      cell: ({ row }) =>
        row.original.phone ? (
          <a className="hover:underline" href={`tel:${row.original.phone}`}>
            {row.original.phone}
          </a>
        ) : (
          '—'
        )
    },
    {
      accessorKey: 'note',
      header: createColumnHeader(t('partner.contacts.columns.note')),
      cell: ({ row }) => (
        <span className="line-clamp-2 text-muted-foreground">{row.original.note ?? ''}</span>
      )
    },
    {
      id: 'outcome',
      meta: { fitContent: true },
      header: createColumnHeader(t('partner.contacts.columns.outcome')),
      cell: ({ row }) => (
        <ContactOutcomeCell
          contact={row.original}
          matchedLead={leadMatches.get(row.original._id)}
          isSalesActivated={isSalesActivated}
          onSubmitLead={setLeadSource}
          onNoInterest={setClosing}
        />
      )
    },
    createActionsColumn<PartnerContact>({
      onEdit: (contact) => {
        setEditing(contact);
        setDialogOpen(true);
      },
      onDelete: (contact) => setDeletingId(contact._id),
      // A submitted lead is in the CRM and stays; only "no interest" can be undone.
      getCustomActions: (contact) =>
        outcomeOf(contact) === 'noInterest'
          ? [
              {
                label: t('partner.contacts.reopen'),
                icon: <RotateCcw className="h-4 w-4" />,
                onClick: () => reopen(contact)
              }
            ]
          : []
    })
  ];

  return (
    <div className="flex h-full flex-col">
      <PartnerSalesTabsNav />
      <div className="flex min-h-0 flex-1 flex-col gap-6 p-6">
        <PartnerPageHeader
          title={t('partner.contacts.title')}
          subtitle={t('partner.contacts.subtitle')}
          onRefresh={() => refetch()}
          isRefreshing={isFetching}
        >
          <Button variant="outline" onClick={() => setUploadOpen(true)}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {t('partner.contacts.import.button')}
          </Button>
          <Button onClick={openCreate}>
            <UserPlus className="w-4 h-4 mr-2" />
            {t('partner.contacts.add')}
          </Button>
        </PartnerPageHeader>

        {isError ? (
          <PartnerErrorState
            error={error}
            icon={Contact}
            title={t('partner.contacts.errorTitle')}
            description={t('partner.contacts.errorDescription')}
            onRetry={() => refetch()}
          />
        ) : (
          <TooltipProvider delayDuration={0}>
            <DataTable
              columns={columns}
              data={visibleContacts}
              isLoading={isLoading}
              emptyMessage={t(
                contacts.length === 0 ? 'partner.contacts.empty' : 'partner.contacts.emptyFiltered'
              )}
              enableGlobalFilter
              quickFilters={[
                {
                  id: 'status',
                  label: t('partner.contacts.filter.label'),
                  render: () => (
                    <Tabs
                      value={statusFilter}
                      onValueChange={(v) => setStatusFilter(v as StatusFilter)}
                    >
                      <TabsList>
                        <TabsTrigger value="open">{t('partner.contacts.filter.open')}</TabsTrigger>
                        <TabsTrigger value="lead">{t('partner.contacts.filter.lead')}</TabsTrigger>
                        <TabsTrigger value="noInterest">
                          {t('partner.contacts.filter.noInterest')}
                        </TabsTrigger>
                        <TabsTrigger value="all">{t('partner.contacts.filter.all')}</TabsTrigger>
                      </TabsList>
                    </Tabs>
                  )
                }
              ]}
            />
          </TooltipProvider>
        )}

        <PartnerContactDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          contact={editing}
          isSubmitting={createContact.isPending || updateContact.isPending}
          onSubmit={handleSubmit}
        />

        <DeleteConfirmDialog
          open={deletingId !== null}
          onOpenChange={(open) => !open && setDeletingId(null)}
          onConfirm={() => {
            if (!deletingId) return;
            deleteContact.mutate(deletingId, {
              onSuccess: () => setDeletingId(null),
              onError: showError
            });
          }}
          isDeleting={deleteContact.isPending}
          entityPhrase={t('partner.contacts.deleteEntityPhrase')}
        />

        <NewLeadFlow
          open={leadSource !== null}
          onOpenChange={(open) => !open && setLeadSource(null)}
          prefill={
            leadSource
              ? {
                  firstName: leadSource.firstName,
                  lastName: leadSource.lastName,
                  email: leadSource.email,
                  phone: leadSource.phone,
                  company: leadSource.company
                }
              : undefined
          }
          contactId={leadSource?._id}
        />

        <ExcelUploadDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          title={t('partner.contacts.import.title')}
          description={t('partner.contacts.import.description')}
          downloadTemplate={downloadPartnerContactsTemplate}
          showSuccessToast={false}
          parse={async (file) => {
            const preview = previewContactImport(
              await parsePartnerContactsFromExcel(file),
              contacts
            );
            setImportPreview(preview);
            return preview.rows.length;
          }}
        />

        <NoInterestDialog
          contact={closing}
          isSubmitting={updateContact.isPending}
          onClose={() => setClosing(null)}
          onConfirm={closeAsNoInterest}
        />

        <ContactImportPreviewDialog
          preview={importPreview}
          onClose={() => setImportPreview(null)}
        />
      </div>
    </div>
  );
}
